// ------------------------------------------------------------------------------
// name: image
// author: mudas
// created: 2025/2/12
// ------------------------------------------------------------------------------

import { CropOp, ResizeOp } from 'common/types';

export interface AppendImageOptions {
  resize?: ResizeOp;
  crop?: CropOp;
  format?: string;

  // 质量参数
  quality?: number;
}

export const isImageFile = (fileName: string) => {
  // 图片常用格式
  const reg = /\.(png|jpg|jpeg|gif|tiff|bmp|ico|webp)$/i;
  return reg.test(fileName);
};

/**
 * 检查是否为阿里云 OSS 域名
 */
const isAliyunOSSDomain = (hostname: string): boolean => {
  return (
    hostname.includes('tiangong-oss.fuxi.netease.com') ||
    hostname.includes('fuxi-yl-tiangong.oss-cn-hangzhou.aliyuncs.com')
  );
};

/**
 * 构建阿里云 OSS 图片处理参数
 */
const buildOSSImageProcess = (op: AppendImageOptions): string => {
  const processes: string[] = [];

  // 处理 resize 参数
  if (op.resize) {
    const { mode, width, height } = op.resize;
    let resizeParams: string[] = [];

    switch (mode) {
      case 'lfit': // 等比缩放至指定宽高区域内最大图形
        resizeParams.push(`m_lfit`);
        if (width) resizeParams.push(`w_${width}`);
        if (height) resizeParams.push(`h_${height}`);
        break;
      case 'mfit': // 等比缩放至覆盖指定宽高区域
        resizeParams.push(`m_mfit`);
        if (width) resizeParams.push(`w_${width}`);
        if (height) resizeParams.push(`h_${height}`);
        break;
      case 'fill': // 等比缩放至覆盖指定宽高区域并居中裁剪
        resizeParams.push(`m_fill`);
        if (width) resizeParams.push(`w_${width}`);
        if (height) resizeParams.push(`h_${height}`);
        break;
      case 'pad': // 等比缩放至指定宽高内最大图形并填充颜色
        resizeParams.push(`m_pad`);
        if (width) resizeParams.push(`w_${width}`);
        if (height) resizeParams.push(`h_${height}`);
        break;
      case 'fixed': // 固定宽高，强制缩放
        resizeParams.push(`m_fixed`);
        if (width) resizeParams.push(`w_${width}`);
        if (height) resizeParams.push(`h_${height}`);
        break;
      case 'percent': // 按百分比缩放
        if (width) resizeParams.push(`p_${width}`); // 使用 width 作为百分比值
        break;
      default:
        // 默认使用 lfit 模式
        resizeParams.push(`m_lfit`);
        if (width) resizeParams.push(`w_${width}`);
        if (height) resizeParams.push(`h_${height}`);
    }

    if (resizeParams.length > 0) {
      processes.push(`resize,${resizeParams.join(',')}`);
    }
  }

  // 处理 crop 参数
  if (op.crop) {
    const { width, height, g, x, y } = op.crop;
    let cropParams: string[] = [];

    if (width) cropParams.push(`w_${width}`);
    if (height) cropParams.push(`h_${height}`);

    // 处理重心位置
    if (g) {
      cropParams.push(`g_${g}`);
    }

    // 处理坐标偏移
    if (x !== undefined) cropParams.push(`x_${x}`);
    if (y !== undefined) cropParams.push(`y_${y}`);

    if (cropParams.length > 0) {
      processes.push(`crop,${cropParams.join(',')}`);
    }
  }

  // 处理 format 参数
  if (op.format) {
    const supportedFormats = ['jpg', 'png', 'webp', 'bmp', 'gif', 'tiff', 'heic', 'avif'];
    if (supportedFormats.includes(op.format.toLowerCase())) {
      processes.push(`format,${op.format.toLowerCase()}`);
    }
  }

  // 处理 quality 参数
  if (op.quality) {
    processes.push(`quality,q_${op.quality}`);
  }

  return processes.join('/');
};

/**
 * 构建传统图片处理参数
 */
const buildTraditionalImageOp = (op: AppendImageOptions): string => {
  const paramsObj = {
    resize: op.resize ? `${op.resize.mode}:${op.resize.width}:${op.resize.height}:0` : null,
    crop: op.crop
      ? `${op.crop.width}:${op.crop.height}:${op.crop.g || ''}${op.crop.x ? `:${op.crop.x}` : ''}${op.crop.y ? `:${op.crop.y}` : ''}`
      : null,
    quality: op.quality ? `${op.quality}` : null,
    format: op.format,
  };

  const keys = Object.keys(paramsObj);
  return keys
    .filter((key) => paramsObj[key])
    .map((key) => `${key}:${paramsObj[key]}`)
    .join(',');
};

export const appendImageOp = (url: string, op?: AppendImageOptions, timestamp?: string) => {
  const urlObj = new URL(url);

  // 添加时间戳参数
  if (timestamp) {
    urlObj.searchParams.set('t', timestamp);
  }

  // 如果没有操作参数，直接返回
  if (!op) {
    return urlObj.toString();
  }

  // 根据域名选择不同的参数格式
  if (isAliyunOSSDomain(urlObj.hostname)) {
    // 阿里云 OSS 格式：?x-oss-process=image/resize,w_100,h_100/format,jpg/quality,q_80
    const ossProcess = buildOSSImageProcess(op);
    if (ossProcess) {
      urlObj.searchParams.set('x-oss-process', `image/${ossProcess}`);
    }
  } else {
    // 传统格式：?op=resize:lfit:100:100:0,format:jpg,quality:80
    const traditionalOp = buildTraditionalImageOp(op);
    if (traditionalOp) {
      urlObj.searchParams.set('op', traditionalOp);
    }
  }

  return urlObj.toString();
};
